"use client"

import { cn } from "@/lib/utils"

interface TypingIndicatorProps {
  /** "thinking" = shimmering text (bot). "dots" = three bouncing dots (human). */
  mode: "thinking" | "dots"
  /** Label for assistive tech + visible text when mode="thinking". */
  label: string
}

/**
 * Chat typing indicator. Two intentionally different treatments to
 * communicate who's on the other end:
 *
 *   - thinking → bot: "Thinking…" text with a left-to-right gradient
 *                     sweep, matching current LLM UX conventions.
 *   - dots     → human: three bouncing dots, matching chat-app UX.
 */
export function TypingIndicator({ mode, label }: TypingIndicatorProps) {
  if (mode === "thinking") {
    return (
      <span
        role="status"
        aria-label={label}
        className={cn(
          // Gradient sweep: dim base colour with a bright band cycling
          // across it. Base + highlight both navy-family so the effect
          // matches the bubbles (deep-blue-200 base, deep-blue-950 band).
          "type-body-sm inline-block bg-clip-text font-medium text-transparent",
          "bg-[linear-gradient(90deg,var(--color-deep-blue-200)_0%,var(--color-deep-blue-200)_40%,var(--color-deep-blue-950)_50%,var(--color-deep-blue-200)_60%,var(--color-deep-blue-200)_100%)]",
          "bg-[length:300%_100%]"
        )}
        style={{ animation: "chat-thinking-shimmer 1.8s linear infinite" }}
      >
        {label}
      </span>
    )
  }

  return (
    <span
      role="status"
      aria-label={label}
      className="inline-flex items-center gap-1"
    >
      <span className="bg-deep-blue-400 h-1.5 w-1.5 animate-bounce rounded-full" />
      <span
        className="bg-deep-blue-400 h-1.5 w-1.5 animate-bounce rounded-full"
        style={{ animationDelay: "0.15s" }}
      />
      <span
        className="bg-deep-blue-400 h-1.5 w-1.5 animate-bounce rounded-full"
        style={{ animationDelay: "0.3s" }}
      />
    </span>
  )
}
